import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Pagination } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  SearchAction,
  InterestAction,
  PhotoreqAction,
} from "../../store/actions";

import useScreenSize from "../../utils/usescreensize";
import RemoveEmptyKeys from "../../utils/removeemptykeys";
import {
  getArrayParam,
  getNumberParam,
  getStringParam,
  setNumberParam,
  setStringParam,
} from "../../utils/urlsearchparams";
import DashboardCard from "../../components/dashboardcard/dashboardcard";
import CardBottomButton from "../../components/dashboardcard/cardbottombutton";
import EditProfileDropdown from "../../components/dropdown/editprofiledropdown";

import Cardloader from "../../components/loader/cardloader";
import Spacer from "../../components/spacer/spacer";
import UnderHeaderMenu from "../../components/underheadermenu";
import DashboardMobileCard from "../../components/dashboardcard/dashboardmobilecard";

import images from "../../assets";

import "./styles.css";

const BasicSearch = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const screenSize = useScreenSize();
  const [searchParams, setSearchParams] = useSearchParams();

  const { basicMatches, basicCount, isBasicLoading } = useSelector(
    (state) => state.search
  );
  const gender = useSelector((state) => state.profile?.profile?.gender);

  const { interestsAll } = useSelector((state) => state.interests);
  const { photoreqAll } = useSelector((state) => state.photoreq);

  const pageNo = getNumberParam(searchParams, "page", 1);
  const pageSize = getNumberParam(searchParams, "size", 8);
  const sortParam = getStringParam(searchParams, "sort", "desc");
  const filterParamsKey = [
    "ageMin",
    "ageMax",
    "country",
    "stateProvince",
    "maritalStatus",
    "spokenLanguage",
    "typeofProfile",
    "activityStatus",
  ]
    .map((key) => searchParams.getAll(key).join(","))
    .join("|");
  const [sortOption, setSortOption] = useState(sortParam);
  const [matchedUsers, setMatchedUsers] = useState([]);

  useEffect(() => {
    setMatchedUsers(basicMatches);
  }, [basicMatches]);

  useEffect(() => {
    const searchData = RemoveEmptyKeys({
      ageMin: searchParams.has("ageMin")
        ? getNumberParam(searchParams, "ageMin", 18)
        : null,
      ageMax: searchParams.has("ageMax")
        ? getNumberParam(searchParams, "ageMax", 30)
        : null,
      country: getArrayParam(searchParams, "country", []),
      stateProvince: getArrayParam(searchParams, "stateProvince", []),
      maritalStatus: getArrayParam(searchParams, "maritalStatus", []),
      spokenLanguage: getArrayParam(searchParams, "spokenLanguage", []),
      typeofProfile: getArrayParam(searchParams, "typeofProfile", []),
      activityStatus: getArrayParam(searchParams, "activityStatus", []),
    });
    const payload = {
      searchData,
      from: (pageNo - 1) * pageSize,
      size: pageSize,
      gender,
      sort: sortParam,
    };
    if (gender) {
      dispatch(SearchAction.basicSearch(payload));
    }
  }, [dispatch, filterParamsKey, gender, pageNo, pageSize, sortParam]);

  useEffect(() => {
    setSortOption(sortParam);
  }, [sortParam]);

  const handlePageChange = (pageNumber) => {
    const params = new URLSearchParams(searchParams);
    setNumberParam(params, "page", pageNumber);
    setSearchParams(params);
  };

  const handleSortChange = (value) => {
    setSortOption(value);
    const params = new URLSearchParams(searchParams);
    setStringParam(params, "sort", value);
    setNumberParam(params, "page", 1);
    setSearchParams(params);
  };

  const handleSendInterest = (id) => {
    dispatch(InterestAction.sendInterest({ receiverId: id }));
  };

  const handleSendPhotoReq = (id) => {
    dispatch(PhotoreqAction.sendPhotoreq({ receiverId: id }));
  };

  return (
    <>
      {screenSize.width < 768 && (
        <UnderHeaderMenu onClick={() => navigate("/dashboard")}>
          <div style={{ width: "150px" }}>
            <EditProfileDropdown
              data={[
                { label: "Newest First", value: "desc" },
                { label: "Oldest First", value: "asc" },
              ]}
              onChange={handleSortChange}
              value={sortOption}
              placeholder="Sort by"
            />
          </div>
        </UnderHeaderMenu>
      )}
      <div className="recommended-container">
        <div className="header-container standard-search">
          <h1 className="section-heading">Search Results</h1>
          {screenSize.width > 768 && (
            <div style={{ width: "150px" }}>
              <EditProfileDropdown
                data={[
                  { label: "Newest First", value: "desc" },
                  { label: "Oldest First", value: "asc" },
                ]}
                onChange={handleSortChange}
                value={sortOption}
                placeholder="Sort by"
              />
            </div>
          )}
        </div>

        {isBasicLoading ? (
          <Cardloader />
        ) : (
          <>
            {matchedUsers?.length === 0 && (
              <div className="flex flex-col justify-center items-center p-10 w-full gap-4">
                <img
                  src={images.noSearchIcon}
                  alt="no notifications"
                  className="h-[160px] lg:h-full"
                />
                <div className="text-basePrimary font-bold text-[24px] lg:text-[32px]">
                  No Results Found
                </div>
                <p className="text-[14px] text-center max-w-[500px]">
                  Try changing your filters or search again with different
                  criteria.
                </p>
              </div>
            )}

            {screenSize.width < 768 &&
              matchedUsers?.map((item, index) => {
                const interestSent = interestsAll?.some(
                  (profile) => profile.receiverId === item?.username
                );
                return (
                  <DashboardMobileCard
                    item={item}
                    index={index}
                    key={index}
                    interest={interestSent}
                  />
                );
              })}

            {screenSize.width > 768 && (
              <div className="recommended-matches-container">
                {matchedUsers?.map((item, index) => {
                  const interestSent = interestsAll?.some(
                    (profile) => profile.receiverId === item?.username
                  );
                  const photoreqSent = photoreqAll?.some(
                    (profile) => profile.receiverId === item?.username
                  );
                  return (
                    <DashboardCard
                      item={item}
                      index={index}
                      key={index}
                      redirectedfrom="standardSearch"
                    >
                      {item?.matchOfTheDay && (
                        <CardBottomButton icon={images.cardButtonTick} />
                      )}
                      <CardBottomButton
                        icon={images.cardMessage}
                        onClick={() =>
                          navigate(
                            `/messages?username=${item?.username}&firstName=${item?.firstName}&lastName=${item?.lastName}&photo=${item?.profilePicture}`
                          )
                        }
                        title="Send Message"
                      />
                      <CardBottomButton
                        icon={images.cardPhotoReq}
                        onClick={() => handleSendPhotoReq(item?.username)}
                        title="Send Photo Request"
                        disabled={photoreqSent}
                      />
                      <CardBottomButton
                        icon={images.cardmatch}
                        onClick={() => handleSendInterest(item?.username)}
                        title="Send Interest"
                        disabled={interestSent}
                      />
                    </DashboardCard>
                  );
                })}
              </div>
            )}
          </>
        )}
        <Spacer height="36px" />
        <div className="flex justify-center items-center">
          <Pagination
            current={pageNo}
            total={basicCount}
            defaultPageSize={pageSize}
            onChange={handlePageChange}
            showSizeChanger={false}
          />
        </div>
        <Spacer height="24px" />
      </div>
    </>
  );
};

export default BasicSearch;
